// Pure helpers for Site Progress reports (backend: common/progress_reports.py).
import { shortDate } from './siteShareModel';
import { localDay } from '../../lib/localDay';

export const MAX_MILESTONES = 12;
export const REPORT_EXPIRY = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '1 year' },
];

const iso = (d) => localDay(d);

// First and last dated, non-failed visit; else the last 30 days.
export function defaultRange(timeline, today = new Date()) {
  const days = ((timeline && timeline.visits) || [])
    .filter((v) => v && v.date && v.status !== 'failed')
    .map((v) => String(v.date).slice(0, 10))
    .sort();
  if (days.length) return { date_from: days[0], date_to: days[days.length - 1] };
  const from = new Date(today.getTime() - 30 * 86400000);
  return { date_from: iso(from), date_to: iso(today) };
}

export const MAX_LINES = 8;
export const SCHEDULES = [
  { value: '', label: 'Not stated' },
  { value: 'on_track', label: 'On schedule' },
  { value: 'ahead', label: 'Ahead of schedule' },
  { value: 'behind', label: 'Behind schedule' },
];

const blankInvestor = { schedule: '', target_completion: '', forecast_completion: '', budget: '', spent_to_date: '', next_steps: '', risks: '' };

export const MAX_SOV_LINES = 40;
export const MAX_ISSUES = 60;
export const CONTROL_OPTIONS = [
  { value: '', label: 'Not stated' },
  { value: 'gcp', label: 'Ground control points' },
  { value: 'rtk', label: 'RTK/PPK, no ground control' },
  { value: 'none', label: 'No ground control' },
];

export const blankDetails = () => ({ lines: [], issues: [], capture: {}, change: null });
export const blankLine = () => ({ name: '', value: '', percent: '', refs: '', evidence: [], note: '' });
export const blankIssue = () => ({ ref: '', title: '', location: '', photo: '', status: 'open', opened: '', closed: '', note: '' });
export const blankMilestone = () => ({ text: '', done: false, planned: '', actual: '' });

export function emptyForm(timeline, today) {
  return { kind: 'progress', title: '', ...defaultRange(timeline, today), percent: '', milestones: [], summary: '', prepared_for: '', prepared_by: '', details: blankDetails(), revision_note: '' };
}

const numText = (n) => (n === null || n === undefined ? '' : String(n));

// "E07, E10" for file ids, from the report's evidence index.
export function refsFor(ids, view) {
  const ev = (view && view.evidence) || [];
  return (ids || []).map((id) => (ev.find((e) => e.did === id) || {}).ref).filter(Boolean).join(', ');
}

// File ids for "E07, e10 E12"; unknown refs come back in `missing`.
export function idsForRefs(text, view) {
  const ev = (view && view.evidence) || [];
  const want = String(text || '').toUpperCase().split(/[\s,;]+/).filter(Boolean);
  const ids = [];
  const missing = [];
  for (const r of want) {
    const e = ev.find((x) => String(x.ref || '').toUpperCase() === r);
    if (!e || !e.did) missing.push(r);
    else if (!ids.includes(e.did)) ids.push(e.did);
  }
  return { ids, missing };
}

// The builder's details as form state. view (optional) turns file ids into E refs.
export function detailsForm(d, view) {
  const x = d || {};
  const cap = {};
  Object.entries(x.capture || {}).forEach(([vid, c]) => {
    cap[vid] = { aircraft: c.aircraft || '', pilot_cert: c.pilot_cert || '', altitude_m: numText(c.altitude_m), control: c.control || '', gcp_count: numText(c.gcp_count) };
  });
  return {
    lines: (x.lines || []).map((l) => ({ name: l.name || '', value: moneyText(l.value), percent: numText(l.percent), refs: refsFor(l.evidence, view), evidence: [...(l.evidence || [])], note: l.note || '' })),
    issues: (x.issues || []).map((i) => ({ ref: i.ref || '', title: i.title || '', location: i.location || '', photo: i.photo || '', status: i.status === 'closed' ? 'closed' : 'open', opened: i.opened || '', closed: i.closed || '', note: i.note || '' })),
    capture: cap,
    change: x.change || null,
  };
}

const milestoneForm = (m) => ({ text: m.text || '', done: !!m.done, planned: m.planned || '', actual: m.actual || '' });

// 'New report': from the day after the last one, its lines, open issues and milestones carried forward (backend suggest_progress).
export function progressFormFromSuggestion(s, view) {
  const x = s || {};
  return {
    kind: 'progress',
    title: x.title || '',
    date_from: x.date_from || '',
    date_to: x.date_to || '',
    percent: numText(x.percent_complete),
    milestones: (x.milestones || []).map(milestoneForm),
    summary: '',
    prepared_for: x.prepared_for || '',
    prepared_by: x.prepared_by || '',
    details: detailsForm(x.details, view),
    revision_note: '',
  };
}

const moneyText = (n) => (n === null || n === undefined || n === '' ? '' : Number(n).toLocaleString('en-CA'));

function investorForm(inv) {
  const i = inv || {};
  return {
    schedule: i.schedule || '',
    target_completion: i.target_completion || '',
    forecast_completion: i.forecast_completion || '',
    budget: moneyText(i.budget),
    spent_to_date: moneyText(i.spent_to_date),
    next_steps: (i.next_steps || []).join('\n'),
    risks: (i.risks || []).join('\n'),
  };
}

// 'New monthly update': last month, carried forward from the last update (backend suggest_update).
export function investorFormFromSuggestion(s) {
  const x = s || {};
  return {
    kind: 'investor',
    title: x.title || '',
    date_from: x.date_from || '',
    date_to: x.date_to || '',
    percent: x.percent_complete === null || x.percent_complete === undefined ? '' : String(x.percent_complete),
    milestones: [],
    summary: '',
    prepared_for: x.prepared_for || '',
    prepared_by: x.prepared_by || '',
    investor: x.investor ? investorForm(x.investor) : { ...blankInvestor },
  };
}

const lines = (t) => String(t || '').split('\n').map((l) => l.trim()).filter(Boolean);
const money = (t) => {
  const v = String(t ?? '').replace(/[$,\s]/g, '');
  return v === '' ? null : Math.round(Number(v));
};

export function formFromReport(r, view) {
  if (!r) return null;
  return {
    kind: r.kind || 'progress',
    ...(r.kind === 'investor' ? { investor: investorForm(r.investor) } : {}),
    title: r.title || '',
    date_from: r.date_from || '',
    date_to: r.date_to || '',
    percent: r.percent_complete === null || r.percent_complete === undefined ? '' : String(r.percent_complete),
    milestones: (r.milestones || []).map(milestoneForm),
    summary: r.summary || '',
    prepared_for: r.prepared_for || '',
    prepared_by: r.prepared_by || '',
    details: detailsForm(r.details, view),
    revision_note: '',
  };
}

// '' when the form is fine, else one plain sentence.
export function formError(f, view) {
  if (!f.date_from || !f.date_to) return 'Pick a start and end date.';
  if (f.date_from > f.date_to) return 'The start date is after the end date.';
  if (String(f.percent).trim() !== '') {
    const n = Number(f.percent);
    if (!Number.isFinite(n) || n < 0 || n > 100) return '% complete is a number from 0 to 100.';
  }
  if ((f.milestones || []).filter((m) => m.text.trim()).length > MAX_MILESTONES) return `Up to ${MAX_MILESTONES} milestones.`;
  for (const [i, m] of (f.milestones || []).entries()) {
    if (!m.text.trim() && (m.planned || m.actual)) return `Milestone ${i + 1} needs a name.`;
  }
  const dErr = detailsError(f.details, view);
  if (dErr) return dErr;
  if (f.kind === 'investor') {
    const i = f.investor || {};
    for (const [k, what] of [['budget', 'The budget'], ['spent_to_date', 'Spent to date']]) {
      const n = money(i[k]);
      if (n !== null && (!Number.isFinite(n) || n < 0)) return `${what} is an amount in dollars.`;
    }
    if (lines(i.next_steps).length > MAX_LINES) return `Up to ${MAX_LINES} next steps.`;
    if (lines(i.risks).length > MAX_LINES) return `Up to ${MAX_LINES} risks.`;
    if (lines(i.next_steps).concat(lines(i.risks)).some((l) => l.length > 160)) return 'Keep each line under 160 characters.';
  }
  return '';
}

const filled = (l) => [l.name, l.value, l.percent, l.refs, l.note].some((v) => String(v || '').trim());

// '' when the builder's details are fine, else one plain sentence.
export function detailsError(d, view) {
  if (!d) return '';
  const ls = (d.lines || []).filter(filled);
  if (ls.length > MAX_SOV_LINES) return `Up to ${MAX_SOV_LINES} lines.`;
  for (const [i, l] of ls.entries()) {
    const n = `Line ${i + 1}`;
    if (!l.name.trim()) return `${n} needs a name.`;
    if (String(l.percent).trim() !== '') {
      const p = Number(l.percent);
      if (!Number.isFinite(p) || p < 0 || p > 100) return `${n}: % complete is a number from 0 to 100.`;
    }
    const v = money(l.value);
    if (v !== null && (!Number.isFinite(v) || v < 0)) return `${n}: the value is an amount in dollars.`;
    if (view && String(l.refs || '').trim()) {
      const { missing } = idsForRefs(l.refs, view);
      if (missing.length) return `${n}: ${missing.join(', ')} ${missing.length === 1 ? "isn't" : "aren't"} in this report's evidence index.`;
    }
  }
  const is = (d.issues || []).filter((x) => [x.title, x.location, x.note].some((v) => String(v || '').trim()));
  if (is.length > MAX_ISSUES) return `Up to ${MAX_ISSUES} issues.`;
  for (const [i, x] of is.entries()) {
    const n = x.ref || `Issue ${i + 1}`;
    if (!x.title.trim()) return `${n} needs a short description.`;
    if (x.status === 'closed' && x.opened && x.closed && x.closed < x.opened) return `${n} closes before it opens.`;
  }
  for (const c of Object.values(d.capture || {})) {
    if (String(c.altitude_m || '').trim() !== '') {
      const a = Number(c.altitude_m);
      if (!Number.isFinite(a) || a < 1 || a > 500) return 'Height flown is 1 to 500 m.';
    }
    if (c.control === 'gcp' && String(c.gcp_count || '').trim() !== '') {
      const g = Number(c.gcp_count);
      if (!Number.isInteger(g) || g < 1 || g > 500) return 'Ground control points is a count from 1 to 500.';
    }
  }
  return '';
}

export function detailsPayload(d, view) {
  const x = d || blankDetails();
  const capture = {};
  Object.entries(x.capture || {}).forEach(([vid, c]) => {
    const row = {
      aircraft: String(c.aircraft || '').trim() || null,
      pilot_cert: String(c.pilot_cert || '').trim() || null,
      altitude_m: String(c.altitude_m || '').trim() === '' ? null : Number(c.altitude_m),
      control: c.control || null,
      gcp_count: c.control === 'gcp' && String(c.gcp_count || '').trim() !== '' ? Number(c.gcp_count) : null,
    };
    if (Object.values(row).some((v) => v !== null)) capture[vid] = row;
  });
  return {
    lines: (x.lines || []).filter(filled).map((l) => ({
      name: l.name.trim(),
      value: money(l.value),
      percent: String(l.percent).trim() === '' ? null : Math.round(Number(l.percent)),
      evidence: view ? idsForRefs(l.refs, view).ids : [...(l.evidence || [])],
      note: String(l.note || '').trim() || null,
    })),
    issues: (x.issues || []).filter((i) => i.title.trim()).map((i) => ({
      ref: i.ref || null,
      title: i.title.trim(),
      location: String(i.location || '').trim() || null,
      photo: i.photo || null,
      status: i.status === 'closed' ? 'closed' : 'open',
      opened: i.opened || null,
      closed: i.status === 'closed' ? (i.closed || null) : null,
      note: String(i.note || '').trim() || null,
    })),
    capture,
    change: x.change || null,
  };
}

// withDetails: false while the details column isn't in the database yet.
export function payloadFromForm(f, view, { withDetails = true } = {}) {
  const pct = String(f.percent).trim();
  return {
    title: f.title.trim() || null,
    date_from: f.date_from,
    date_to: f.date_to,
    percent_complete: pct === '' ? null : Math.round(Number(pct)),
    milestones: (f.milestones || []).filter((m) => m.text.trim()).map((m) => ({ text: m.text.trim(), done: !!m.done, planned: m.planned || null, actual: m.actual || null })),
    summary: f.summary.trim() || null,
    prepared_for: String(f.prepared_for || '').trim() || null,
    prepared_by: String(f.prepared_by || '').trim() || null,
    ...(f.kind === 'investor' ? { kind: 'investor', investor: investorPayload(f.investor) } : {}),
    ...(withDetails && f.details ? { details: detailsPayload(f.details, view) } : {}),
    ...(String(f.revision_note || '').trim() ? { revision_note: f.revision_note.trim() } : {}),
  };
}

function investorPayload(i) {
  const x = i || {};
  return {
    schedule: x.schedule || null,
    target_completion: x.target_completion || null,
    forecast_completion: x.forecast_completion || null,
    budget: money(x.budget),
    spent_to_date: money(x.spent_to_date),
    next_steps: lines(x.next_steps),
    risks: lines(x.risks),
  };
}

// "Since No. 001 (Sep 30, 2026): +12 points complete, $540,000 spent, 2 site visits this period."
export function sinceLine(view) {
  const inv = view && view.investor;
  if (!inv) return '';
  const s = inv.since;
  if (!s) return 'First investor update for this project.';
  const parts = [];
  if (s.percent_delta !== null && s.percent_delta !== undefined) {
    const n = s.percent_delta;
    parts.push(n ? `${n > 0 ? '+' : ''}${n} points complete` : '% complete unchanged');
  }
  if (s.spent_delta !== null && s.spent_delta !== undefined) {
    const n = s.spent_delta;
    parts.push(n >= 0 ? `$${n.toLocaleString('en-CA')} spent` : `spend revised down $${(-n).toLocaleString('en-CA')}`);
  }
  const v = (view.visits || []).length;
  parts.push(`${v} site visit${v === 1 ? '' : 's'} this period`);
  if (s.forecast_before) parts.push(`forecast moved from ${dayLabel(s.forecast_before)}`);
  const ref = s.report_no ? `No. ${String(s.report_no).padStart(3, '0')}` : 'the last update';
  return `Since ${ref} (${dayLabel(s.date_to)}): ${parts.join(', ')}.`;
}

export function kindName(v) {
  return (v && v.kind_name) || (v && v.kind === 'investor' ? 'Investor update' : 'Progress report');
}

// "Link live until Oct 31, 2026 · 3 views", "Not shared", ...
export function shareLine(share) {
  if (!share || share.status === 'none') return 'Not shared';
  const n = Number(share.view_count) || 0;
  const views = `${n} view${n === 1 ? '' : 's'}`;
  if (share.status === 'revoked') return `Link turned off · ${views}`;
  if (share.status === 'expired') return `Link expired ${shortDate(share.expires_at)} · ${views}`;
  return `Link live until ${shortDate(share.expires_at)} · ${views}`;
}

export function sortReports(reports) {
  return [...(reports || [])].sort((a, b) => String(b.date_to || '').localeCompare(String(a.date_to || ''))
    || String(b.created_at || '').localeCompare(String(a.created_at || '')));
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "Oct 1, 2026": a plain YYYY-MM-DD day as is; a timestamp as its Toronto day.
export function dayLabel(day) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(localDay(day || ''));
  if (!m) return '';
  return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}

// "Sep 1, 2026 to Oct 31, 2026" for list rows.
export function rangeLine(r) {
  if (!r || !r.date_from || !r.date_to) return '';
  return r.date_from === r.date_to ? dayLabel(r.date_from) : `${dayLabel(r.date_from)} to ${dayLabel(r.date_to)}`;
}

export function pdfName(view) {
  const slug = String((view && view.project && view.project.name) || 'site').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'site';
  const stem = view && view.kind === 'investor' ? 'investor-update' : 'progress-report';
  return `${stem}-${slug}-${view?.date_from || ''}-to-${view?.date_to || ''}.pdf`;
}

// "No. 003" once the report has a number, else "Draft".
export function reportNoLabel(view) {
  const n = Number(view && view.report_no);
  return Number.isInteger(n) && n > 0 ? `No. ${String(n).padStart(3, '0')}` : 'Draft';
}

// "Visit 04" for a visit number on the site, else ''.
export function visitNo(n) {
  const k = Number(n);
  return Number.isInteger(k) && k > 0 ? `Visit ${String(k).padStart(2, '0')}` : '';
}

// "Aug 20 - Oct 16, 2026" (year once when both ends share it).
export function periodLabel(a, b) {
  const fa = dayLabel(a);
  const fb = dayLabel(b);
  if (!fa || !fb) return fa || fb || '';
  if (fa === fb) return fa;
  if (String(a).slice(0, 4) === String(b).slice(0, 4)) return `${fa.slice(0, -6)} - ${fb}`;
  return `${fa} - ${fb}`;
}

// Spend and forecast changes, which only investor updates carry (backend report_pdf.investor_since_items).
export function investorSinceItems(inv) {
  const s = (inv && inv.since) || {};
  const out = [];
  if (s.spent_delta !== null && s.spent_delta !== undefined) {
    const n = s.spent_delta;
    out.push(n >= 0 ? `$${n.toLocaleString('en-CA')} spent` : `Spend revised down $${(-n).toLocaleString('en-CA')}`);
  }
  if (s.forecast_before) {
    const to = inv.forecast_completion;
    out.push(`Forecast completion moved from ${dayLabel(s.forecast_before)}${to ? ` to ${dayLabel(to)}` : ''}`);
  }
  return out;
}

// The largest 1, 2 or 5 x 10^n metres not over target.
export function niceLength(target) {
  if (!(target > 0)) return 0;
  const p = 10 ** Math.floor(Math.log10(target));
  for (const k of [5, 2, 1]) if (k * p <= target * (1 + 1e-9)) return k * p;
  return p;
}

const trim0 = (n) => String(Number(n.toFixed(3)));
export function lengthLabel(m) {
  if (m >= 1000) return `${trim0(m / 1000)} km`;
  if (m >= 1) return `${trim0(m)} m`;
  return `${trim0(m * 100)} cm`;
}

// A scale bar for a map `width` px wide whose ground is scale.ground_w_m wide:
// about a fifth of the map, never under 96px, with a nice length.
export function scaleBar(scale, width) {
  const g = scale && Number(scale.ground_w_m);
  if (!(g > 0)) return null;
  let m;
  if (width > 0) {
    const share = Math.min(0.45, Math.max(0.22, 96 / width));
    m = niceLength(g * share);
  } else {
    m = Number(scale.bar_m) || niceLength(g * 0.22);
  }
  if (!(m > 0)) return null;
  const frac = m / g;
  return { frac, meters: m, label: lengthLabel(m), half: !width || frac * width >= 110 ? lengthLabel(m / 2) : '' };
}

export function bytesLabel(n) {
  const v = Number(n) || 0;
  for (const [unit, k] of [['GB', 1024 ** 3], ['MB', 1024 ** 2], ['KB', 1024]]) {
    if (v >= k) return `${(v / k).toLocaleString('en-CA', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ${unit}`;
  }
  return `${v} B`;
}

export const numLabel = (n) => String(Number(Number(n).toFixed(3)));
const roundBig = (v) => (v >= 100 ? Math.round(v / 10) * 10 : Math.round(v)).toLocaleString('en-CA');
export const volumeText = (m3) => `${roundBig(Number(m3) || 0)} m³`;
export function areaText(m2) {
  const v = Number(m2) || 0;
  if (v >= 10000) return `${(v / 10000).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ha`;
  return `${roundBig(v)} m²`;
}
export function signedVolumeText(m3) {
  const v = Number(m3) || 0;
  return v > 0 ? `+${volumeText(v)}` : v < 0 ? `-${volumeText(-v)}` : volumeText(0);
}

// details.change from a measureChange summary between two point clouds
// (view.compare_clouds), with its method line.
export function changeFromSummary(clouds, summary, method, today = new Date()) {
  const pick = (c) => ({ deliverable_id: c.deliverable_id, date: c.date });
  return {
    from: pick(clouds.from),
    to: pick(clouds.to),
    fill_m3: Math.round(summary.fill * 10) / 10,
    cut_m3: Math.round(summary.cut * 10) / 10,
    changed_area_m2: Math.round(summary.changedArea),
    compared_area_m2: Math.round(summary.comparedArea),
    cell_m: Math.round(summary.cell * 100) / 100,
    min_change_m: summary.minChange,
    method,
    measured_on: iso(today),
  };
}

// True when the saved cut and fill still compares the range's first and last clouds.
export function changeIsCurrent(change, clouds) {
  if (!change || !clouds || !clouds.from || !clouds.to) return false;
  return change.from && change.to && change.from.deliverable_id === clouds.from.deliverable_id && change.to.deliverable_id === clouds.to.deliverable_id;
}
